import { usdCadRateAt } from "./data/fx-loader";
import { primeRateAt } from "./data/rates-loader";
import { monthlyFactorFromCagr, monthlyRateFromNominalMonthly } from "./rates";
import type { LoanSource, Scenario, SimResult, SimStep, StockSource } from "./types";

/* ──────────────────────────────────────────────────────────────────────────
 * Simulator primitive — pure function.
 *
 * Routes to one of two internal paths based on `scenario.stock.kind`:
 *
 *   - **synthetic**: monthly loop using a CAGR-derived monthly factor. The
 *     hello-world test path. Each step compounds the portfolio by the CAGR
 *     monthly factor and capitalizes interest on the loan (if enabled).
 *
 *   - **real**: daily loop over a real price series. The portfolio holds
 *     `units = borrowed / firstPrice` and is valued daily at `units × close`.
 *     Interest still accrues monthly (HELOC convention): on the first
 *     trading day of each new calendar month, the prior month's interest
 *     capitalizes onto the loan balance.
 *
 * Both paths emit `SimStep` rows with identical shapes. The chart and KPI
 * code doesn't care which kind of scenario produced them — same downstream.
 *
 * Deterministic, no side effects, no env/clock dependencies.
 * ────────────────────────────────────────────────────────────────────────── */

export function runSimulation(scenario: Scenario): SimResult {
  const series =
    scenario.stock.kind === "synthetic"
      ? runSynthetic(scenario, scenario.stock)
      : runReal(scenario, scenario.stock);

  return {
    scenario,
    series,
    final: series[series.length - 1]!,
  };
}

// ── Synthetic path ──────────────────────────────────────────────────────────

function runSynthetic(
  scenario: Scenario,
  stock: Extract<StockSource, { kind: "synthetic" }>,
): SimStep[] {
  // Synthetic scenarios have symbolic dates, so they can't meaningfully
  // look up historical Prime. Require a flat loan rate.
  if (scenario.loanRate.kind !== "flat") {
    throw new Error(
      "runSimulation: synthetic stock scenarios require a flat loan rate (historical doesn't apply to symbolic dates).",
    );
  }
  const monthlyReturnFactor = monthlyFactorFromCagr(stock.cagr);
  const monthlyLoanRate = monthlyRateFromNominalMonthly(scenario.loanRate.rate);

  const series: SimStep[] = [];
  let portfolioValue = scenario.borrowed;
  let loanBalance = scenario.borrowed;

  series.push({
    step: 0,
    date: monthIndexToPhantomDate(0),
    portfolioValue,
    loanBalance,
    equity: portfolioValue - loanBalance,
    interestAccruedThisMonth: 0,
    cashDividendThisStep: 0,
  });

  for (let m = 1; m <= stock.months; m++) {
    portfolioValue = portfolioValue * monthlyReturnFactor;

    const interestAccruedThisMonth = loanBalance * monthlyLoanRate;
    if (scenario.capitalizeInterest) {
      loanBalance = loanBalance + interestAccruedThisMonth;
    }

    series.push({
      step: m,
      date: monthIndexToPhantomDate(m),
      portfolioValue,
      loanBalance,
      equity: portfolioValue - loanBalance,
      interestAccruedThisMonth,
      cashDividendThisStep: 0,
    });
  }

  return series;
}

// ── Real-data path ──────────────────────────────────────────────────────────

function runReal(scenario: Scenario, stock: Extract<StockSource, { kind: "real" }>): SimStep[] {
  if (stock.series.length === 0) {
    throw new Error(`runSimulation: real-data series for ${stock.ticker} is empty`);
  }

  // For USD tickers, the borrowed CAD is converted to USD at the first
  // FX rate, then used to buy USD units. The simulator operates in USD
  // for the stock side and converts to CAD for reporting at each step.
  // For CAD tickers, no conversion happens — units are just borrowed/firstClose.
  const fxSeries = stock.fxSeries;
  const initialPrice = stock.series[0]!.close;
  let units: number;
  if (fxSeries) {
    const firstFx = usdCadRateAt(fxSeries, stock.series[0]!.date);
    const usdBorrowed = scenario.borrowed / firstFx;
    units = usdBorrowed / initialPrice;
  } else {
    units = scenario.borrowed / initialPrice;
  }

  // Build a date → $/unit lookup for fast ex-div day checks during the loop.
  // Populated unconditionally so the simulator can record `cashDividendThisStep`
  // for downstream UIs regardless of dividend mode. The reinvestment behavior
  // itself remains gated on `dripEnabled` further down. Dividend amounts are
  // in the ticker's native currency.
  const dripEnabled = stock.dividendMode === "drip";
  const dividendByDate = new Map<string, number>();
  for (const d of stock.dividends) {
    dividendByDate.set(d.date, d.amount);
  }

  const series: SimStep[] = [];
  let loanBalance = scenario.borrowed;

  // Initial state (day 0) — portfolioValue equals borrowed by construction
  // for both currency cases (the FX conversion at t=0 is exactly inverse
  // of the unit purchase, so units × firstClose × firstFx === borrowed).
  series.push({
    step: 0,
    date: stock.series[0]!.date,
    portfolioValue: scenario.borrowed,
    loanBalance,
    equity: 0,
    interestAccruedThisMonth: 0,
    cashDividendThisStep: 0,
  });

  for (let i = 1; i < stock.series.length; i++) {
    const today = stock.series[i]!;
    const prev = stock.series[i - 1]!;

    // DRIP: on an ex-dividend day, the cash dividend (units × $/unit) is
    // immediately reinvested at today's close, increasing the unit count.
    // This happens BEFORE valuation so portfolioValue reflects the new
    // unit count. DRIP operates entirely in the ticker's native currency
    // (USD dividends buy USD units at USD close), so the FX rate doesn't
    // factor in here. See MATH.md for the conservation analysis.
    //
    // Defensive guard: skip DRIP if today.close is non-positive. Yahoo's
    // historical series occasionally contains a stray close=0 row (data
    // glitch, not a real market event). Dividing dividendCash by 0 would
    // explode units to Infinity and break the rest of the simulation.
    // refresh-prices.mjs filters these out at ingestion as the primary
    // defence; this guard is the second layer in case any slip through.
    // Cash equivalent of any dividend landing today (units × $/unit). Captured
    // on the step regardless of mode so downstream UIs can surface the income
    // stream without re-deriving it. With DRIP enabled, this amount is also
    // reinvested into units immediately below. With `ignore` mode (current
    // default for explainer-page scenarios) the cash isn't reinvested; the
    // chart's portfolio value reflects price-only growth and the simulator
    // doesn't model where the cash goes.
    let cashDividendThisStep = 0;
    const divAmount = dividendByDate.get(today.date);
    if (divAmount !== undefined && divAmount > 0 && today.close > 0) {
      cashDividendThisStep = units * divAmount;
      if (dripEnabled) {
        units = units + cashDividendThisStep / today.close;
      }
    }

    // Portfolio value for this step. CAD tickers value in CAD directly;
    // USD tickers value in USD then convert to CAD at today's FX rate.
    const portfolioValue = fxSeries
      ? units * today.close * usdCadRateAt(fxSeries, today.date)
      : units * today.close;

    // Capitalize interest on the FIRST trading day of each new calendar month
    // (i.e., when today's year-month differs from prev's year-month). Uses
    // the loan balance carried in from the previous step. The monthly rate
    // is constant for flat-loan scenarios and looked up per-month-end-day
    // (forward-fill on Prime + spread) for historical-loan scenarios.
    let interestAccruedThisMonth = 0;
    if (yearMonthOf(today.date) !== yearMonthOf(prev.date)) {
      const monthlyLoanRate = monthlyLoanRateAt(scenario.loanRate, today.date);
      interestAccruedThisMonth = loanBalance * monthlyLoanRate;
      if (scenario.capitalizeInterest) {
        loanBalance = loanBalance + interestAccruedThisMonth;
      }
    }

    series.push({
      step: i,
      date: today.date,
      portfolioValue,
      loanBalance,
      equity: portfolioValue - loanBalance,
      interestAccruedThisMonth,
      cashDividendThisStep,
    });
  }

  return series;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Resolve the monthly loan rate to apply at a given date.
 *
 * - For `flat` loans: returns the constant `rate / 12` (same as Slice 5 and
 *   earlier; the date argument is ignored).
 * - For `historical` loans: forward-fills the Prime series to find the rate
 *   in effect on `date`, adds the user's spread, divides by 12. The result
 *   is the nominal-monthly rate that accrues to the loan that month.
 */
function monthlyLoanRateAt(loanSource: LoanSource, date: string): number {
  if (loanSource.kind === "flat") {
    return monthlyRateFromNominalMonthly(loanSource.rate);
  }
  const prime = primeRateAt(loanSource.primeSeries, date);
  return (prime + loanSource.spread) / 12;
}

/** Extract "YYYY-MM" from an ISO date string. */
function yearMonthOf(isoDate: string): string {
  return isoDate.slice(0, 7);
}

/** For synthetic scenarios, fabricate a stable ISO date per month index so
 *  the chart still has a `date` field to render. Anchored at 2000-01-01 +
 *  month index. These dates aren't real — they're symbolic placeholders. */
function monthIndexToPhantomDate(monthIndex: number): string {
  const baseYear = 2000;
  const totalMonths = monthIndex;
  const year = baseYear + Math.floor(totalMonths / 12);
  const month = (totalMonths % 12) + 1;
  return `${year}-${String(month).padStart(2, "0")}-01`;
}
